<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Hero Section -->
    <section id="home" class="hero hero-eastern">
      <div class="container">
        <div class="hero-content fade-in-up">
          <h1>Изучение Китайского Онлайн</h1>
          <p>Мы подберем для вас лучшего преподавателя по китайскому языку и погрузим в изучение не выходя из дома.</p>
          <div class="hero-buttons">
            <UIButton to="/contacts#contact-methods">Отправить заявку</UIButton>
            <UIButton to="/contacts#contact-methods" variant="outline">пробный урок бесплатно</UIButton>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <UISection title="Почему выбирают нас">
      <template #default>
        <div class="features-grid">
          <div class="feature-card card card-padded">
            <div class="feature-icon">
              <img :src="icons.teacher" alt="Преподаватель" />
            </div>
            <h3>Преподаватель под ваш уровень</h3>
            <p>Наши преподаватели специализируется на определенном уровне владения языка. Вам будет комфортно и интересно.</p>
          </div>
          <div class="feature-card card card-padded">
            <div class="feature-icon">
              <img :src="icons.group" alt="Группа" />
            </div>
            <h3>Группы до 2-3 человек</h3>
            <p>Вы можете приходить на занятия с друзьями или мы сами найдем вам группу, чтобы сделать занятия для вас дешевле.</p>
          </div>
          <div class="feature-card card card-padded">
            <div class="feature-icon">
              <img :src="icons.time" alt="Гибкий график" />
            </div>
            <h3>Занимайтесь где и когда удобно</h3>
            <p>
              Подберем удобное для вас время и график занятий. Занятия проходят онлайн через
              <a href="https://besedapp.ru/" target="_blank" rel="noopener noreferrer">Беседу</a>
              либо в другом удобном для вас месте по согласованию с преподавателем.
            </p>
          </div>
        </div>
      </template>
    </UISection>

    <!-- Additional Info Section -->
    <UISection>
      <template #default>
        <div style="text-align: center; max-width: 800px; margin: 0 auto;">
          <p style="font-size: 1.2rem; color: #718096; margin-bottom: 2rem;">
            Напишите нам в любой удобный для вас мессенджер, мы ответим на все интересующие вас вопросы и согласуем бесплатный пробный 30-минутный урок.
          </p>
          <UIButton to="/contacts#contact-methods">Отправить заявку</UIButton>
        </div>
      </template>
    </UISection>

    <!-- Testimonial Section -->
    <section v-if="homeReviews.length > 0" class="testimonial">
      <div class="container">
        <div class="testimonial-header">
          <h2 class="section-title" style="color: white;">Отзывы учеников</h2>
          <router-link to="/reviews" class="testimonial-link">Все отзывы</router-link>
        </div>
        <div class="testimonial-slider-wrapper" @mouseenter="stopSlider" @mouseleave="startSlider">
          <div class="testimonial-slider" :style="{ transform: `translateX(-${currentSlide * 100}%)` }">
          <div
            class="testimonial-slide"
            v-for="(review, index) in homeReviews"
            :key="review.id || index"
          >
            <div class="testimonial-content">
              <div v-if="review.media_url" class="testimonial-media" @click="openMedia(review)">
                <img v-if="review.media_type === 'image'" :src="review.media_url" alt="Превью" />
                <div v-else class="video-preview">
                  <video :src="review.media_url" muted></video>
                  <span class="play-icon">▶</span>
                </div>
              </div>
              <div class="testimonial-text-content">
                <div class="testimonial-text">{{ review.text }}</div>
                <div class="testimonial-author">{{ review.student_name }}</div>
              </div>
            </div>
          </div>
          </div>
        </div>
        <div class="slider-controls">
          <button class="slider-dot" v-for="(review, idx) in homeReviews" :key="idx" :class="{ active: idx === currentSlide }" @click="goToSlide(idx)"></button>
        </div>
      </div>
    </section>

    <ReviewMediaModal
      v-if="modal.visible"
      :src="modal.src"
      :type="modal.type"
      @close="closeModal"
    />

    <!-- CTA Section -->
    <section id="contact" class="cta-section">
      <div class="container">
        <h2>Готовы начать изучение китайского?</h2>
        <p>Свяжитесь с нами и получите бесплатный пробный урок</p>
        <UIButton href="/contacts#contact-methods">Связаться</UIButton>
      </div>
    </section>

    <!-- Footer -->
    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UISection from '@/components/ui/UISection.vue'
import UIFooter from '@/components/ui/UIFooter.vue'
import iconTeacher from '@/assets/icons/teacher.png'
import iconGroup from '@/assets/icons/group.png'
import iconTime from '@/assets/icons/time.png'
import ReviewMediaModal from '@/components/ui/ReviewMediaModal.vue'
import axios from 'axios'
import { addSchemaScript, getOrganizationSchema, getWebSiteSchema, clearAllSchemas } from '@/utils/schema'

const api = axios.create({
  baseURL: '/api/v1'
})

export default {
  name: 'HomePage',
  components: {
    AppHeader,
    UIButton,
    UISection,
    UIFooter,
    ReviewMediaModal
  },
  data() {
    return {
      icons: {
        teacher: iconTeacher,
        group: iconGroup,
        time: iconTime
      },
      homeReviews: [],
      modal: {
        visible: false,
        src: '',
        type: 'image'
      },
      currentSlide: 0,
      sliderInterval: null
    }
  },
  computed: {
    sliderStyle() {
      return {
        '--slide': this.currentSlide
      }
    }
  },
  mounted() {
    // Добавляем Schema.org разметку для Яндекс
    clearAllSchemas()
    addSchemaScript(getOrganizationSchema(), 'organization')
    addSchemaScript(getWebSiteSchema(), 'website')

    // Smooth scrolling for navigation links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
      anchor.addEventListener('click', function (e) {
        e.preventDefault();
        const target = document.querySelector(this.getAttribute('href'));
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      });
    });

    this.startSlider();
    this.loadReviews();

    // Add animation on scroll
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('fade-in-up');
        }
      });
    }, observerOptions);

    document.querySelectorAll('.feature-card, .testimonial-slider').forEach(el => {
      observer.observe(el);
    });
  },
  beforeUnmount() {
    this.stopSlider();
  },
  methods: {
    async loadReviews() {
      try {
        const { data } = await api.get('/public/reviews/random', {
          params: { count: 3 }
        })
        this.homeReviews = data || []
        this.currentSlide = 0
      } catch (err) {
        console.error('Не удалось загрузить отзывы для главной', err)
      }
    },
    startSlider() {
      if (this.sliderInterval) return
      this.sliderInterval = setInterval(() => {
        this.currentSlide = (this.currentSlide + 1) % this.homeReviews.length
      }, 6000)
    },
    stopSlider() {
      if (this.sliderInterval) {
        clearInterval(this.sliderInterval)
        this.sliderInterval = null
      }
    },
    goToSlide(index) {
      this.currentSlide = index
      this.stopSlider()
      this.startSlider()
    },
    openMedia(review) {
      if (!review.media_url) return
      this.modal = {
        visible: true,
        src: review.media_url,
        type: review.media_type || 'image'
      }
    },
    closeModal() {
      this.modal.visible = false
      this.modal.src = ''
    }
  }
}
</script>

<style scoped>
.hero-eastern::after {
  content: '';
  position: absolute;
  inset: 0;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320"><path fill="%23f5f1ef" fill-opacity="1" d="M0,128L48,138.7C96,149,192,171,288,186.7C384,203,480,213,576,197.3C672,181,768,139,864,117.3C960,96,1056,96,1152,122.7C1248,149,1344,203,1392,229.3L1440,256L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"></path></svg>');
  background-repeat: no-repeat;
  background-position: bottom center;
  background-size: cover;
  opacity: 0.6;
  pointer-events: none;
}

.testimonial {
  padding: 80px 0;
  background: #111827;
  color: #f1f5f9;
}

.testimonial-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
}

.testimonial-link {
  color: rgba(255,255,255,0.75);
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s ease;
}

.testimonial-link:hover {
  color: #ffffff;
}

.testimonial-slider-wrapper {
  overflow: hidden;
}

.testimonial-slider {
  display: flex;
  transition: transform 0.6s cubic-bezier(0.65, 0, 0.35, 1);
}

.testimonial-slide {
  flex: 0 0 100%;
  padding: 2.5rem;
  background: rgba(15, 23, 42, 0.45);
  border-radius: 18px;
  border: 1px solid rgba(100, 116, 139, 0.35);
  box-shadow: 0 25px 50px -35px rgba(15, 23, 42, 0.8);
  color: #e2e8f0;
  min-height: 250px;
  display: flex;
  align-items: center;
}

.testimonial-content {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
  min-height: 200px;
}

.testimonial-media {
  width: 120px;
  height: 160px;
  border-radius: 12px;
  overflow: hidden;
  background: rgba(229, 231, 235, 0.1);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.testimonial-media:hover {
  transform: scale(1.02);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
}

.testimonial-media img,
.testimonial-media video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.video-preview {
  position: relative;
}

.play-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 1.8rem;
  color: rgba(255, 255, 255, 0.9);
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
}

.testimonial-text-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  justify-content: center;
  padding: 0.5rem 0;
}

.testimonial-slide:last-child {
  margin-right: 0;
}

.testimonial-text {
  font-size: 1.15rem;
  line-height: 1.6;
  color: rgba(248,250,252,0.95);
  margin: 0;
  font-weight: 400;
}

.testimonial-author {
  font-weight: 700;
  letter-spacing: 0.05em;
  color: rgba(231, 76, 60, 0.9);
  text-transform: uppercase;
  font-size: 0.85rem;
  margin: 0;
  align-self: flex-start;
}

.slider-controls {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 2rem;
  flex-wrap: wrap;
}


.slider-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  border: none;
  background: rgba(255,255,255,0.35);
  cursor: pointer;
  transition: transform 0.2s ease, background 0.2s ease;
}

.slider-dot.active {
  background: #e74c3c;
  transform: scale(1.2);
}

.slider-dot:hover {
  background: rgba(255,255,255,0.6);
}

@media (max-width: 768px) {
  .testimonial-slide {
    padding: 2rem;
  }
  
  .testimonial-content {
    flex-direction: column;
    gap: 1.5rem;
    align-items: center;
    text-align: center;
    min-height: auto;
  }
  
  .testimonial-media {
    width: 100px;
    height: 140px;
    order: -1;
  }
  
  .testimonial-text-content {
    align-items: center;
    padding: 0;
  }
  
  .testimonial-author {
    align-self: center;
  }
}

.feature-card {
  background: #ffffff;
  text-align: center;
}

.feature-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1rem;
}

.feature-icon img {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

</style>
